/**
 * Screen share and camera from the screens: says calmly why something was
 * refused or ended (toasts, §4.7 Errors), and Watch from another channel
 * (joins that channel first, password included, then watches: §1).
 */
import type { Snowflake, VoiceUnpublishReason } from '@neosocial/protocol';
import { tr } from '../i18n';
import { onMediaNotice, watchStream, type MediaNotice } from '../voice/live';
import { useVoice } from '../voice/store';
import { toast } from '../ui/overlay';
import { askStream } from './streamView';
import { joinVoiceFromUi } from './voiceUi';

/** The words for a refusal of `source` (`denied` reason). */
export function deniedKey(
  source: 'screen' | 'camera',
  reason: string,
): { key: string; n?: number } | null {
  const cam = source === 'camera';
  switch (reason) {
    case 'media_limit':
      return { key: cam ? 'stream.err.camLimit' : 'stream.err.limit' };
    case 'not_allowed':
      return { key: 'stream.noPerm' };
    case 'feature_off':
      return { key: cam ? 'stream.err.soon' : 'stream.err.off' };
    case 'no_stream':
      return { key: 'stream.err.noStream' };
    case 'unsupported':
      return { key: cam ? 'stream.err.camUnsupported' : 'stream.err.unsupported' };
    case 'unknown_request':
      return { key: cam ? 'stream.err.camUnsupported' : 'stream.err.old' };
    default:
      return { key: 'stream.err.unknown' };
  }
}

/** The words for comms-voice ending your publication (`unpublished`); none for a move. */
export function endedKey(source: 'screen' | 'camera', reason: VoiceUnpublishReason): string | null {
  const cam = source === 'camera';
  switch (reason) {
    case 'over_limit':
      return cam ? 'stream.err.camOverLimit' : 'stream.err.overLimit';
    case 'not_allowed':
      return 'stream.noPerm';
    case 'feature_off':
      return cam ? 'stream.err.soon' : 'stream.err.off';
    case 'no_media':
      return cam ? 'stream.err.camNoMedia' : 'stream.err.noMedia';
    case 'staff':
      return cam ? 'stream.err.camStaff' : 'stream.err.staff';
    default:
      return null;
  }
}

/** A notice as a toast's words, or null when nothing needs saying. */
export function noticeText(n: MediaNotice): string | null {
  const { t } = tr();
  switch (n.kind) {
    case 'denied': {
      const d = deniedKey(n.source, n.reason);
      return d ? t(d.key, { n: n.limit }) : null;
    }
    case 'ended': {
      const k = endedKey(n.source, n.reason);
      return k ? t(k) : null;
    }
    case 'no_stream':
      return t('stream.err.noStream');
    case 'capture_failed':
      if (n.source === 'screen') return t('stream.err.capture');
      return t(n.name === 'NotAllowedError' ? 'stream.err.camBlocked' : 'stream.err.camera');
  }
}

onMediaNotice((n) => {
  const text = noticeText(n);
  if (!text) return;
  const icon = n.kind !== 'no_stream' && n.source === 'camera' ? 'video' : 'monitor';
  toast(text, { icon, tone: 'warn' });
});

/**
 * Watch `userId`'s stream in `channelId`: from another channel (or out of
 * voice) it joins there first, by the normal join (a password prompt
 * included), then watches, the stream focused (big) in its room as the
 * tile's Watch opens it (Discord; QA V-10: the sidebar's Watch stream left
 * it a grid tile). Resolves true once you watch.
 */
export async function joinAndWatch(channelId: Snowflake, userId: Snowflake): Promise<boolean> {
  const s = useVoice.getState().session;
  if (s.channelId !== channelId || s.status !== 'connected') {
    const ok = await joinVoiceFromUi(channelId);
    if (!ok) return false;
  }
  const now = useVoice.getState().session;
  const m = Object.values(now.members).find((x) => x.user_id === userId && x.channel === channelId);
  if (!m?.screen) {
    toast(tr().t('stream.err.noStream'), { icon: 'monitor', tone: 'warn' });
    return false;
  }
  askStream(channelId, userId);
  return watchStream(m.pid, true);
}
